import { useState } from "react";
import { useSelector } from "react-redux";

import { ITEM_CALCULATED_FIELDS } from "appConfig";

import { Decimal } from "classes/DecimalClasses";

import { DeleteLine } from "components/EditPage/DeleteLine";
import { QuickAddItem } from "components/QuickAddItem";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineClass } from "components/formFields/line/LineClass";
import { LineDescription } from "components/formFields/line/LineDescription";
import { LineInventoryItemBin } from "components/formFields/line/LineInventoryItemBin";
import { LineJobWorkcenter } from "components/formFields/line/LineJobWorkcenter";
import { LineLotSelect } from "components/formFields/line/LineLotSelect";
import { LineOnHand } from "components/formFields/line/LineOnHand";
import { LineQuantity } from "components/formFields/line/LineQuantity";
import { LineSerialsSelect } from "components/formFields/line/LineSerialsSelect";
import { LineUom } from "components/formFields/line/LineUom";

import {
  createLineFromItem,
  postItemAndCreateLineItem,
  getItemRecord,
  clearItemDataFromLine,
} from "services/sosInventoryService/domainLogic";
import { updateTransferLineWithItem } from "services/sosInventoryService/inventoryTransaction/domainLogic";
import { getEmptyRecord as getEmptyItemRecord } from "services/sosInventoryService/item/schema";
import { afterTouchLine } from "services/sosInventoryService/transfer/afterTouchLine";
import { setPageDirty } from "services/utility/edit";
import { isLineFieldInError } from "services/utility/errors";
import { formatLotsWithQuantityAndExpiration } from "services/utility/formatting";
import { TYPES } from "services/utility/schema";

import { useItemLocationLots } from "hooks/useItemLocationLots";
import { usePlans } from "hooks/usePlans";

import { OBJECT_TYPES } from "appConstants";
import { EMPTY_LINE_ITEM, LINE_ITEM_FIELDS } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.TRANSFER.fullString;

// created this wrapper to make drag and drop code easier to see
export function LineItem(props) {
  return <LineItemDetails {...props} />;
}

function LineItemDetails(props) {
  const {
    objectType,
    record,
    line,
    lineHandler,
    expandItemGroup,
    classes,
    items,
    toBins,
    relatedRecords,
    jobWorkcenters,
    itemsCount,
    addItem,
    lineItemsErrors: errors,
    setErrors,
  } = props;
  const { fromLocation, date } = record;

  const [addNewItem, setAddNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [lineLoading, setLineLoading] = useState(false);
  const { hasAtLeastPlusPlan } = usePlans();

  // SETTINGS
  const defaultAssetAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultAssetAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultCOGSAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultCOGSAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultExpenseAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultExpenseAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultIncomeAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultIncomeAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultTaxCode = useSelector(
    (state) => state.userCompanySettings.settings.defaultTaxCode,
    (prev, curr) => curr?.id === prev?.id
  );

  const rawLots = useItemLocationLots(
    line.item?.id,
    fromLocation?.name,
    line.bin?.id,
    date,
    line.itemDetails.lotTracking
  );
  const lots = rawLots ? formatLotsWithQuantityAndExpiration(rawLots) : null;

  function isInError(field) {
    return isLineFieldInError(field, line.lineNumber, errors);
  }

  function handleValueChange(fieldName, newValue) {
    let newLine;
    const lineItemFields = LINE_ITEM_FIELDS[OBJECT_TYPE];
    if (lineItemFields[TYPES.reference].includes(fieldName)) {
      newLine = { ...line, [fieldName]: { id: newValue } };
    } else if (lineItemFields[TYPES.decimal].includes(fieldName)) {
      newLine = { ...line, [fieldName]: new Decimal(newValue || 0) };
    } else {
      newLine = { ...line, [fieldName]: newValue };
    }
    setPageDirty();
    lineHandler({ type: "update", updatedLine: newLine });
  }

  function handleReferenceChange(fieldName, newValue) {
    const newLine = { ...line, [fieldName]: newValue };
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: newLine,
      fieldName,
    });
  }

  async function handleItemChange(newItem, userInput) {
    if (!newItem) {
      const updatedLine = clearItemDataFromLine(line);
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
      return;
    }
    if (newItem?.id === "add") {
      return openAddItem(userInput);
    }
    setLineLoading(true);
    const item = await getItemRecord(
      newItem.id,
      fromLocation?.id,
      date,
      ITEM_CALCULATED_FIELDS[OBJECT_TYPE]
    );
    const newLine = await updateTransferLineWithItem(
      item,
      line,
      record,
      toBins
    );
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, "item"),
    });
    setLineLoading(false);
  }

  function handleUomChange(_, uom) {
    setPageDirty();
    const updatedLine = afterTouchLine({ ...line, uom }, "uom");
    lineHandler({ type: "update", updatedLine });
  }

  async function onQuickAddItem(name) {
    const newItemSettings = {
      defaultCOGSAccount,
      defaultAssetAccount,
      defaultExpenseAccount,
      defaultIncomeAccount,
      defaultTaxCode,
    };
    const item = { ...getEmptyItemRecord(newItemSettings), name };
    const updatedLine = await postItemAndCreateLineItem(
      item,
      EMPTY_LINE_ITEM[objectType],
      line.lineNumber,
      addItem,
      setErrors
    );
    if (updatedLine) {
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
    }
    setAddNewItem(false);
  }

  function onAddItem(item) {
    setAddNewItem(false);
    const updatedLine = createLineFromItem(
      item,
      EMPTY_LINE_ITEM[objectType],
      line.lineNumber,
      lineHandler
    );
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
    addItem();
  }

  function handleDelete() {
    setPageDirty();
    lineHandler({ type: "delete", deleteAt: line.lineNumber });
  }

  function openAddItem(userInput) {
    // handle "Add new item" in Item select input field; this does not change the
    // line item, so no need to proceed further into the handler
    setNewItemName(userInput);
    setAddNewItem(true);
  }

  if (!line) {
    return null;
  }

  return (
    <>
      <DeleteLine onClick={handleDelete} />
      <LineTableCell>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 0.5rem",
          }}
        >
          {line.lineNumber}
        </div>
      </LineTableCell>
      <LineInventoryItemBin
        lineLoading={lineLoading}
        toBin={line.toBin} // TODO: Update once API v3 released - fromBin is mapped differently between transfer and rental
        fromBin={line.fromBin} // TODO: Update once API v3 released - fromBin is mapped differently between transfer and rental
        toBins={toBins}
        bins={line.availableBins}
        fromLocation={relatedRecords.fromLocation}
        toLocation={relatedRecords.toLocation}
        handleItemChange={handleItemChange}
        handleReferenceChange={handleReferenceChange}
        objectType={OBJECT_TYPE}
        line={line}
        items={items}
        itemsCount={itemsCount}
        addItem={addItem}
        allowExpand
        expandItemGroup={expandItemGroup}
        isInError={isInError}
      />
      <LineTableCell sx={{ minWidth: "24rem" }}>
        <LineDescription
          value={line.description}
          onValueChange={handleValueChange}
          error={isInError("description")}
        />
        {hasAtLeastPlusPlan && (
          <>
            {line.itemDetails?.serialTracking && (
              <LineSerialsSelect
                objectType={OBJECT_TYPE}
                value={line.serials}
                itemId={line.item.id}
                bin={line.fromBin}
                location={relatedRecords.fromLocation}
                quantity={line.quantity}
                onValueChange={handleValueChange}
                error={isInError("serials")}
              />
            )}

            {line.itemDetails?.lotTracking && (
              <LineLotSelect
                value={line.lot}
                lots={lots}
                bin={line.fromBin}
                objectType={objectType}
                location={relatedRecords.fromLocation}
                onValueChange={handleReferenceChange}
              />
            )}
          </>
        )}
      </LineTableCell>
      <LineOnHand value={line.onhand} />
      <LineQuantity
        objectType={OBJECT_TYPE}
        value={line.quantity}
        onValueChange={handleValueChange}
        error={isInError("quantity")}
        disabled={lineLoading}
      />
      <LineUom
        value={line.uom}
        onValueChange={handleUomChange}
        itemUoms={line.itemDetails?.itemUoms}
        error={isInError("uom")}
      />
      <LineClass
        value={line.class}
        classes={classes}
        onValueChange={handleReferenceChange}
      />
      <LineJobWorkcenter
        job={line.job}
        workcenter={line.workcenter}
        value={line.jobWorkcenter}
        jobWorkcenters={jobWorkcenters}
        onValueChange={handleReferenceChange}
      />
      {addNewItem && (
        <QuickAddItem
          open={addNewItem}
          initialNameValue={newItemName}
          onClose={() => setAddNewItem(false)}
          onAdd={onAddItem}
          onQuickAdd={onQuickAddItem}
        />
      )}
    </>
  );
}
